import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'

import { cn } from '@/utils/ui/styles'

import { TruncatedText } from '@/shared/truncation/components/truncated-text'

import { TONE_SURFACE } from './tone'

import type { Tone } from './tone'
import type { UseRenderComponentProps } from '@base-ui/react/use-render'

type PillProps = UseRenderComponentProps<'span'> & {
	/** Which `--status-*` family to render in. Default `neutral`. */
	tone?: Tone
	/**
	 * Free-form leading node — `<PillDot />`, a lucide glyph, a spinner. A
	 * `ReactNode` rather than an enum so `Pill` never imports an icon library
	 * and never grows a case per symbol (ADR 0020 §3).
	 */
	icon?: React.ReactNode
}

/**
 * The app's one status chip (ADR 0020).
 *
 * **One** size and shape — no `size`, no `shape`. Figma draws two heights and
 * two radii; we standardise over that because no call site needs the variation
 * and every prop on a primitive this widely used is a decision every future
 * call site has to make.
 *
 * **Not interactive** — no `onClick`, no dismiss. A removable filter chip is a
 * `Badge` plus a real button, so focus rings and keyboard semantics stay out of
 * a component that has no use for them.
 *
 * Domains do not pass `tone` by hand: each exports one `PillPreset` record and
 * spreads it (see `preset.ts`).
 *
 * @example
 * <Pill tone="success" icon={<PillDot />}>Connected</Pill>
 *
 * @example
 * // polymorphic via Base UI's `render` — no anchor logic inside Pill
 * <Pill tone="pending" render={<a href="/cases/1" />}>Open</Pill>
 */
export const Pill = ({
	className,
	tone = 'neutral',
	icon,
	children,
	render,
	...props
}: PillProps) => {
	return useRender({
		defaultTagName: 'span',
		props: mergeProps<'span'>(
			{
				className: cn(
					'inline-flex h-5.5 w-fit max-w-full shrink-0 items-center gap-1 overflow-hidden rounded-full px-2 text-xs font-medium whitespace-nowrap',
					'[&>svg]:size-3 [&>svg]:shrink-0',
					TONE_SURFACE[tone],
					className,
				),
				children: (
					<>
						{icon}
						{/* The label is its own element for two reasons: it is what
						 * truncates (the pill's height must never change), and it keeps a
						 * future `collapse` prop purely additive. */}
						<TruncatedText as="span" className="min-w-0">
							{children}
						</TruncatedText>
					</>
				),
			},
			props,
		),
		render,
		state: { slot: 'pill', tone },
	})
}

/**
 * The tone dot. Takes no props by design — `bg-current` inherits the colour
 * `tone` already set on the pill, so this never couples to the `Tone` type.
 *
 * Decorative: the label carries the meaning, so it is hidden from AT.
 */
export const PillDot = () => {
	return (
		<span
			aria-hidden
			className="size-1.5 shrink-0 rounded-full bg-current"
			data-slot="pill-dot"
		/>
	)
}
